import {
  getVoiceCallSession,
  isTerminalCallPhase,
  subscribeVoiceCallSession,
} from '../../stores/voice-call-store'
import type { VoiceCallSession } from '../../types/voice-call'
import { getNativePlatform, isNativeApp } from '../../utils/platform'
import { updateNativeLockScreenCall, type NativeLockScreenCall } from './native-call'

/**
 * What the native lock-screen call screen shows for `session` (Android). A
 * call that is live on another of this user's devices is over on this one.
 */
export function lockScreenCallSnapshot(
  session: VoiceCallSession | null,
): NativeLockScreenCall | null {
  if (!session?.callId) return null
  const phase: NativeLockScreenCall['phase'] =
    isTerminalCallPhase(session.phase) || session.phase === 'elsewhere'
      ? 'ended'
      : session.phase === 'active'
        ? 'active'
        : 'connecting'
  return {
    callId: session.callId,
    phase,
    muted: Boolean(session.isMuted),
    speaker: Boolean(session.isSpeakerOn),
    video: session.mediaType === 'video',
    ...(session.connectedAt ? { connectedAt: session.connectedAt } : {}),
  }
}

function sameLockScreenCall(a: NativeLockScreenCall, b: NativeLockScreenCall): boolean {
  return (
    a.callId === b.callId &&
    a.phase === b.phase &&
    a.muted === b.muted &&
    a.speaker === b.speaker &&
    a.video === b.video &&
    a.connectedAt === b.connectedAt
  )
}

/**
 * The next report for the native screen, or null when nothing changed. A
 * session that went away reports its last call ended.
 */
export function nextLockScreenCallReport(
  last: NativeLockScreenCall | null,
  session: VoiceCallSession | null,
): NativeLockScreenCall | null {
  const next =
    lockScreenCallSnapshot(session) ??
    (last && last.phase !== 'ended' ? { ...last, phase: 'ended' as const } : null)
  if (!next || (last && sameLockScreenCall(last, next))) return null
  return next
}

/**
 * Android: keep the native lock-screen call screen (and its connect-time
 * audio telemetry) in step with the live call. Returns a stop function.
 */
export function startLockScreenCallSync(): () => void {
  if (!isNativeApp() || getNativePlatform() !== 'android') return () => {}
  let last: NativeLockScreenCall | null = null
  const sync = () => {
    const report = nextLockScreenCallReport(last, getVoiceCallSession())
    if (!report) return
    last = report
    void updateNativeLockScreenCall(report)
  }
  sync()
  const unsubscribe = subscribeVoiceCallSession(sync)
  return () => {
    unsubscribe()
  }
}
